import { Box, Button, Tooltip, useDisclosure } from '@chakra-ui/react';

import AddDeductionAdjustmentModal from 'app/components/DeductionAdjustments/AddDeductionAdjustmentModal';

import useAuthContext from 'app/hooks/useAuthContext';
import useFetchIndividualDeductionsReportsPage from 'app/hooks/useFetchIndividualDeductionsReportsPage';
import useFetchPayCalendar from 'app/hooks/useFetchPayCalendar';

import { AddDeductionAdjustmentButtonProps } from './types';

const OPEN_REPORTS_PAGE_SIZE = 50;

/**
 * Lets an Aslan admin add a deduction adjustment to a person's open deduction report: the earliest report on their pay
 * calendar that has not reached its cut-off. Disabled, saying why, when they have none.
 */
function AddDeductionAdjustmentButton({
  individual = undefined,
}: AddDeductionAdjustmentButtonProps) {
  const { user } = useAuthContext();
  const isAslanAdmin = user?.role === 'ASLAN_ADMIN';
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data: payCalendar } = useFetchPayCalendar(individual?.payCalendarId);
  const { data: openReports } = useFetchIndividualDeductionsReportsPage(
    {
      individualIds: [individual?.id || ''],
      status: ['IN_PROGRESS'],
      page: 0,
      size: OPEN_REPORTS_PAGE_SIZE,
    },
    isAslanAdmin && !!individual?.id
  );

  if (!isAslanAdmin || !individual?.id) {
    return null;
  }

  const openReport = openReports?.list
    .filter((report) => report.payCalendarId === individual.payCalendarId)
    .sort((first, second) => first.startDate.localeCompare(second.startDate))[0];
  const cutoffDate = payCalendar?.payCycles.find(
    (payCycle) => payCycle.id === openReport?.payCycleId
  )?.cutoffDate;

  return (
    <>
      <Tooltip
        label={
          openReport
            ? undefined
            : 'They have no deduction report open for adjustments: their current report has reached its cut-off or does not exist yet.'
        }
        hasArrow
      >
        <Box
          as="span"
          display="inline-block"
          tabIndex={openReport ? undefined : 0}
          data-testid="add-deduction-adjustment-wrapper"
        >
          <Button
            size="sm"
            variant="outline"
            onClick={onOpen}
            isDisabled={!openReport}
            data-testid="add-deduction-adjustment-button"
          >
            Add deduction adjustment
          </Button>
        </Box>
      </Tooltip>
      {openReport && (
        <AddDeductionAdjustmentModal
          isOpen={isOpen}
          onClose={onClose}
          individualId={individual.id}
          individualName={`${individual.firstName} ${individual.lastName}`}
          openReport={{
            id: openReport.id,
            payCycleName: openReport.payCycleName,
            cutoffDate,
            currency: openReport.currency,
            currentDeduction: openReport.salaryDeductions,
          }}
        />
      )}
    </>
  );
}

export default AddDeductionAdjustmentButton;
